import React, { SyntheticEvent, useEffect, useRef, useState } from 'react'
import {
  Box,
  Heading3,
  Display,
  FlexDirection,
  JustifyContent,
  Overflow,
  ModalClose,
  DisplayPosition,
  Divider,
  Color,
  ZIndex,
} from '@gousto-internal/citrus-react'
import { Recipe, RecipeUUID } from '@library/menu-service'
import { Recipes } from './Recipes'
import { DeliveryDetails } from './DeliveryDetails'
import { CheckoutButton } from './CheckoutButton'
import { OrderSummary } from './OrderSummary'
import type { DeliveryPricing } from './types'
import { TransactionalOrderBanner } from './TransactionalOrderBanner'
import { BasketUpsellSection } from './BasketUpsell/BasketUpsellSection'
import type {
  BasketUpsellCarouselContent,
  BasketUpsellProduct,
  BasketUpsellProductActionSource,
} from './BasketUpsell/types'
import { Products } from './Products'
import {
  type FlexPortionTrackingContext,
  trackClickEditRecipeQuantity,
  trackSelectPortionCount,
  trackUpdatePortion,
} from '../../../../../recipe-tile/src/components/Flex'
import { FlexModalOverlay } from '../../../../../recipe-tile/src/components/Flex/FlexModalOverlay'
import { FlexPortionFooter } from '../../../../../recipe-tile/src/components/Flex/FlexPortionFooter'
import { UndoBanner } from '../UndoBanner/UndoBanner'

type BasketUpsell = {
  carousel: BasketUpsellCarouselContent
  products: BasketUpsellProduct[]
  selectedProducts: BasketUpsellProduct[]
  onAddProduct: (productId: string, source: BasketUpsellProductActionSource) => void
  onClickProduct: (product: BasketUpsellProduct) => void
  onRemoveProduct: (productId: string, source: BasketUpsellProductActionSource) => void
}

type BasketRecipe = {
  recipe: Recipe
  quantity: number
  numPortions: number
}

type AddRecipeInfo = {
  position?: string
  source?: 'merch_banner'
  merch_header_type?: 'campaign-banner' | 'compact-campaign-banner'
}

type ContentProps = {
  name: string
  maxRecipesInBox: number
  numPortions: number
  basketRecipes: BasketRecipe[]
  menuUuid?: string | null
  closeModal: () => void
  addRecipe: (
    id: RecipeUUID,
    view: string,
    recipeInfo?: AddRecipeInfo,
    orderId?: string,
    isOrderRecipe?: boolean,
    numPortions?: number,
  ) => void
  removeRecipe: (id: RecipeUUID, view: string) => void
  updateRecipePortions: (id: RecipeUUID, numPortions: number) => void
  onClickRecipe: (id: RecipeUUID) => void
  onCheckout: (e: SyntheticEvent) => void
  deliverySlot: {
    date: string
    timeStart: string
    timeEnd: string
  }
  deliveryAddress: {
    name?: string
    line1: string
    line2: string
    line3: string
    town: string
    postcode: string
    state: string
    county: string
  }
  deliveryPricing: DeliveryPricing
  discountLabel?: string | null
  isCheckoutDisabled: boolean
  isCheckoutPending: boolean
  isTransactionalOrder: boolean
  basketUpsell?: BasketUpsell
  isValueBannerEnabled?: boolean
}

export const Content = ({
  name,
  maxRecipesInBox,
  numPortions,
  basketRecipes,
  menuUuid,
  closeModal,
  addRecipe,
  removeRecipe,
  updateRecipePortions,
  onClickRecipe,
  onCheckout,
  deliverySlot,
  deliveryAddress,
  deliveryPricing,
  discountLabel,
  isCheckoutDisabled,
  isCheckoutPending,
  isTransactionalOrder,
  basketUpsell,
  isValueBannerEnabled = false,
}: ContentProps) => {
  const [lastRemoved, setLastRemoved] = useState<BasketRecipe | null>(null)
  const [editingRecipeId, setEditingRecipeId] = useState<RecipeUUID | null>(null)
  const bannerTimeoutId = useRef<NodeJS.Timeout | null>(null)

  const clearLastRemoved = () => {
    if (bannerTimeoutId.current) {
      clearTimeout(bannerTimeoutId.current)
      bannerTimeoutId.current = null
    }
    setLastRemoved(null)
  }

  const scheduleAutoHide = () => {
    bannerTimeoutId.current = setTimeout(clearLastRemoved, 3000)
  }

  const handleRemove = (recipe: Recipe) => {
    if (bannerTimeoutId.current) {
      clearTimeout(bannerTimeoutId.current)
    }
    const removedBasketRecipe = basketRecipes.find((item) => item.recipe.id === recipe.id)

    setLastRemoved(removedBasketRecipe ?? { recipe, quantity: 1, numPortions })
    setEditingRecipeId(null)
    scheduleAutoHide()
    removeRecipe(recipe.id, 'modal_basket')
  }

  const handleUndo = () => {
    if (!lastRemoved) {
      return
    }
    addRecipe(
      lastRemoved.recipe.id,
      'modal_basket',
      undefined,
      undefined,
      undefined,
      lastRemoved.numPortions,
    )
    clearLastRemoved()
  }

  useEffect(() => () => clearLastRemoved(), [])

  const numberOfRecipesInBasket = basketRecipes.reduce((total, item) => total + item.quantity, 0)
  const isOverLimitBy = numberOfRecipesInBasket - maxRecipesInBox
  const editingRecipe = basketRecipes.find(({ recipe }) => recipe.id === editingRecipeId)
  const getTrackingContext = ({
    recipe,
    numPortions: recipeNumPortions,
  }: BasketRecipe): FlexPortionTrackingContext => ({
    basketRecipeCount: numberOfRecipesInBasket,
    menuUuid,
    recipePortionCount: recipeNumPortions,
    recipeUuid: recipe.id,
    source: 'modal_basket_screen',
  })
  const isFlexPortionEditorOpen = Boolean(editingRecipe)

  const closePortionEditor = () => setEditingRecipeId(null)
  const handleEditRecipe = (recipeId: RecipeUUID) => {
    const basketRecipe = basketRecipes.find(({ recipe }) => recipe.id === recipeId)

    if (!basketRecipe) {
      return
    }

    trackClickEditRecipeQuantity(getTrackingContext(basketRecipe))
    setEditingRecipeId(recipeId)
  }
  const handleUpdatePortions = (updatedNumPortions: number) => {
    if (!editingRecipe) {
      return
    }

    const trackingContext = getTrackingContext(editingRecipe)

    updateRecipePortions(editingRecipe.recipe.id, updatedNumPortions)
    trackUpdatePortion(trackingContext, updatedNumPortions)
    closePortionEditor()
  }

  const handleSelectPortions = (updatedNumPortions: number) => {
    if (!editingRecipe) {
      return
    }

    trackSelectPortionCount(getTrackingContext(editingRecipe), updatedNumPortions)
  }

  const handleRemoveEditingRecipe = () => {
    if (!editingRecipe) {
      return
    }

    handleRemove(editingRecipe.recipe)
  }

  return (
    <Box
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      flexGrow={1}
      height="100%"
      minHeight="0"
      overflowY={Overflow.Hidden}
      position={DisplayPosition.Relative}
    >
      <Box
        flexShrink={0}
        paddingH={5}
        position={DisplayPosition.Relative}
        style={{ paddingTop: '1.2rem' }}
      >
        <ModalClose onClose={closeModal} />
        <Box paddingH={0} paddingBottom={3}>
          <Heading3>{`${name}'s basket`}</Heading3>
        </Box>
      </Box>
      <Divider />
      <Box
        flexGrow={1}
        flexShrink={1}
        minHeight="0"
        overflowY={isFlexPortionEditorOpen ? Overflow.Hidden : Overflow.Scroll}
        paddingV={0}
        paddingH={0}
      >
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          justifyContent={JustifyContent.FlexStart}
          flexGrow={1}
          minHeight={['60vh']}
          paddingH={5}
          paddingV={4}
          gap={6}
          bg={Color.NeutralGrey_50}
        >
          {isTransactionalOrder && <TransactionalOrderBanner />}
          <Recipes
            basketRecipes={basketRecipes}
            removeRecipe={handleRemove}
            onClickRecipe={onClickRecipe}
            onEditRecipe={handleEditRecipe}
            numberOfRecipesInBasket={numberOfRecipesInBasket}
            deliveryDate={deliverySlot.date}
          />
          {basketUpsell && (
            <>
              <BasketUpsellSection
                carousel={basketUpsell.carousel}
                products={basketUpsell.products}
                onAddProduct={basketUpsell.onAddProduct}
                onClickProduct={basketUpsell.onClickProduct}
                onRemoveProduct={basketUpsell.onRemoveProduct}
              />
              <Products
                products={basketUpsell.selectedProducts}
                onAddProduct={basketUpsell.onAddProduct}
                onClickProduct={basketUpsell.onClickProduct}
                onRemoveProduct={basketUpsell.onRemoveProduct}
              />
            </>
          )}
          <OrderSummary
            basketRecipes={basketRecipes}
            deliveryPricing={deliveryPricing}
            isValueBannerEnabled={isValueBannerEnabled}
            numberOfRecipes={numberOfRecipesInBasket}
            shouldShowOrderSummary={numberOfRecipesInBasket <= maxRecipesInBox}
          />
          <DeliveryDetails deliverySlot={deliverySlot} deliveryAddress={deliveryAddress} />
        </Box>
      </Box>
      {isFlexPortionEditorOpen && <FlexModalOverlay onClick={closePortionEditor} />}
      <Box
        display={Display.Flex}
        flexShrink={0}
        width="100%"
        justifyContent={JustifyContent.Center}
        boxShadow="0px -2px 4px rgba(0, 0, 0, 0.15)"
        style={{
          position: 'sticky',
          paddingTop: isFlexPortionEditorOpen ? 0 : '1rem',
          paddingBottom: isFlexPortionEditorOpen ? 0 : '1.5rem',
        }}
        bg={Color.White}
        bottom="0"
        left="0"
        right="0"
        zIndex={ZIndex.Top}
      >
        {lastRemoved && (
          <Box
            position={DisplayPosition.Absolute}
            width="400px"
            top="-50px"
            left="50%"
            style={{ transform: 'translateX(-50%)' }}
          >
            <UndoBanner recipe={lastRemoved.recipe} onUndo={handleUndo} />
          </Box>
        )}
        {editingRecipe ? (
          <FlexPortionFooter
            recipeCount={editingRecipe.quantity}
            numPortions={editingRecipe.numPortions}
            portions={editingRecipe.recipe.portions}
            onUpdatePortions={handleUpdatePortions}
            onSelectPortions={handleSelectPortions}
            onRemoveRecipe={handleRemoveEditingRecipe}
            mode="editor"
            onCloseEditor={closePortionEditor}
          />
        ) : (
          <CheckoutButton
            isOverLimitBy={isOverLimitBy}
            maxRecipesInBox={maxRecipesInBox}
            onCheckout={onCheckout}
            isCheckoutDisabled={!!lastRemoved || isCheckoutDisabled}
            isCheckoutPending={isCheckoutPending}
            discountLabel={discountLabel}
          />
        )}
      </Box>
    </Box>
  )
}
